"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { Link } from "@/navigation";
import { CheckCircle } from "@phosphor-icons/react";

// 阶段数据接口
interface StageItem {
	id: "design" | "prototyping" | "production" | "packaging";
	titleKey: string;
	descriptionKey: string;
	features: {
		titleKey: string;
		descKey: string;
	}[];
	image: string;
}

// 内联 SVG 图标组件 - 产品设计优化
const DesignIcon = ({ className, isActive }: { className?: string; isActive?: boolean }) => (
	<svg viewBox="0 0 64 64" className={className}>
		<circle className={isActive ? "fill-white" : "fill-[#F3F3F3]"} cx="32" cy="32" r="32" />
		<g className={isActive ? "fill-[#65A525]" : "fill-[#8A8A8A]"}>
			<path d="M36.62,43.67h-9.11c-0.71,0-1.28-0.57-1.28-1.28v-2.8c-1.53-1.35-6.08-5.84-6.22-11.44c-0.09-3.43,1.48-6.6,4.66-9.4c0.1-0.08,7.45-6.1,15.51-0.06c0.23,0.19,4.71,3.89,4.63,9.51c-0.06,3.99-2.38,7.84-6.91,11.42v2.76C37.9,43.09,37.33,43.67,36.62,43.67z M28.79,41.1h6.55V39c0-0.4,0.19-0.78,0.5-1.02c4.19-3.19,6.34-6.49,6.4-9.79c0.08-4.37-3.6-7.45-3.64-7.48c-6.37-4.77-12.03-0.21-12.27-0.01c-2.56,2.25-3.83,4.74-3.77,7.38c0.13,5.38,5.68,9.88,5.74,9.92c0.3,0.24,0.48,0.61,0.48,1V41.1z" />
			<path d="M34.4,48h-4.66c-1.93,0-3.5-1.57-3.5-3.5v-2.11c0-0.71,0.57-1.28,1.28-1.28h9.11c0.71,0,1.28,0.57,1.28,1.28v2.11C37.9,46.43,36.33,48,34.4,48z M28.79,43.67v0.83c0,0.52,0.42,0.94,0.94,0.94h4.66c0.52,0,0.94-0.42,0.94-0.94v-0.83H28.79z M32.17,32.96h-0.02c-0.17,0-0.33-0.04-0.49-0.1s-0.3-0.16-0.41-0.28c-1.53-1.58-3.41-3.52-3.62-3.8c-0.43-0.56-0.32-1.37,0.24-1.8c0.56-0.43,1.35-0.33,1.78,0.22c0.18,0.21,1.4,1.49,2.55,2.67l2.39-2.33c0.51-0.49,1.32-0.48,1.81,0.02c0.49,0.51,0.48,1.32-0.02,1.81l-3.31,3.22C32.83,32.83,32.51,32.96,32.17,32.96z" />
			<path d="M32.06,38.84c-0.71,0-1.28-0.57-1.28-1.28v-5.89c0-0.71,0.57-1.28,1.28-1.28c0.71,0,1.28,0.57,1.28,1.28v5.89C33.35,38.27,32.77,38.84,32.06,38.84z" />
		</g>
	</svg>
);

// 内联 SVG 图标组件 - 原型与小批量生产
const PrototypingIcon = ({ className, isActive }: { className?: string; isActive?: boolean }) => (
	<svg viewBox="0 0 64 64" className={className}>
		<circle className={isActive ? "fill-white" : "fill-[#F3F3F3]"} cx="32" cy="32" r="32" />
		<path
			className={isActive ? "fill-[#65A525]" : "fill-[#8A8A8A]"}
			d="M45.33,25.58H18.67c-0.44,0-0.87-0.18-1.18-0.49C17.18,24.78,17,24.36,17,23.92v-5.25c0-0.92,0.75-1.67,1.67-1.67h26.66c0.44,0,0.87,0.18,1.18,0.49C46.82,17.8,47,18.23,47,18.67v5.25c0,0.44-0.18,0.87-0.49,1.18C46.2,25.41,45.77,25.58,45.33,25.58z M19.33,23.25h25.33v-3.9H19.33V23.25z M25.33,47h-6.67c-0.44,0-0.87-0.18-1.18-0.49C17.18,46.2,17,45.77,17,45.33V30.1c0-0.92,0.75-1.67,1.67-1.67h6.67c0.92,0,1.67,0.75,1.67,1.67v15.23C27,46.25,26.25,47,25.33,47z M19.33,44.67h5.33v-13.9h-5.33L19.33,44.67L19.33,44.67z M45.33,47H31.52c-0.44,0-0.87-0.18-1.18-0.49c-0.31-0.31-0.49-0.74-0.49-1.18V30.1c0-0.44,0.18-0.87,0.49-1.18c0.31-0.31,0.74-0.49,1.18-0.49h13.81c0.92,0,1.67,0.75,1.67,1.67v15.23C47,46.25,46.25,47,45.33,47z M32.19,44.67h12.47v-13.9H32.19V44.67z"
		/>
	</svg>
);

// 内联 SVG 图标组件 - 大规模生产
const ProductionIcon = ({ className, isActive }: { className?: string; isActive?: boolean }) => (
	<svg viewBox="0 0 64 64" className={className}>
		<circle className={isActive ? "fill-white" : "fill-[#F3F3F3]"} cx="32" cy="32" r="32" />
		<g className={isActive ? "fill-[#65A525]" : "fill-[#8A8A8A]"}>
			<path d="M46.86,36.87c-0.31,0-0.61-0.18-0.79-0.5c-0.18-0.3-0.19-0.65-0.03-0.91c0.5-0.86,0.21-1.96-0.66-2.45l-1.34-0.77c-0.4-0.23-0.87-0.29-1.32-0.18c-0.45,0.12-0.82,0.41-1.06,0.81c-0.17,0.29-0.49,0.48-0.91,0.48c-0.31,0-0.6-0.17-0.75-0.43c-0.49-0.86-1.59-1.15-2.45-0.66l-1.35,0.78c-0.83,0.48-1.11,1.55-0.64,2.38c0.18,0.29,0.18,0.66-0.01,0.99c-0.17,0.29-0.49,0.48-0.82,0.48c-0.96,0-1.74,0.78-1.74,1.74v1.59c0,0.96,0.78,1.74,1.8,1.74c0.31,0,0.61,0.18,0.79,0.5c0.18,0.3,0.19,0.65,0.03,0.92c-0.24,0.41-0.3,0.9-0.18,1.36c0.13,0.46,0.42,0.85,0.83,1.09l1.34,0.77c0.4,0.23,0.87,0.29,1.32,0.18c0.45-0.12,0.82-0.41,1.06-0.81c0.17-0.29,0.49-0.48,0.85-0.48c0.34,0,0.66,0.19,0.82,0.48c0.23,0.4,0.61,0.69,1.06,0.81c0.15,0.04,0.3,0.06,0.45,0.06c0.3,0,0.6-0.08,0.87-0.23l1.34-0.77c0.41-0.23,0.71-0.63,0.84-1.09c0.13-0.46,0.06-0.95-0.18-1.36c-0.16-0.27-0.15-0.62,0.04-0.94c0.17-0.29,0.46-0.47,0.77-0.47c0.99,0,1.79-0.8,1.79-1.8v-1.5c0-0.99-0.8-1.79-1.79-1.79L46.86,36.87z M46.2,39.58c-0.93,0.19-1.75,0.78-2.25,1.65c-0.5,0.85-0.6,1.87-0.29,2.77l-0.29,0.17c-0.64-0.7-1.56-1.12-2.55-1.12c-0.97,0-1.89,0.42-2.53,1.12L37.99,44c0.3-0.9,0.2-1.92-0.3-2.79c-0.49-0.84-1.3-1.44-2.24-1.63v-0.34c0.93-0.2,1.76-0.79,2.26-1.64c0.49-0.84,0.59-1.85,0.29-2.76l0.29-0.17c0.63,0.7,1.55,1.12,2.56,1.12c0.97,0,1.9-0.42,2.53-1.12l0.29,0.17c-0.3,0.9-0.2,1.92,0.3,2.79c0.49,0.85,1.31,1.45,2.24,1.63v0.33L46.2,39.58z" />
			<path d="M40.81,36.25c-1.75,0-3.17,1.43-3.17,3.17c0,1.74,1.43,3.17,3.17,3.17c1.74,0,3.17-1.43,3.17-3.17C43.98,37.67,42.55,36.25,40.81,36.25z M40.81,40.14c-0.4,0-0.72-0.32-0.72-0.72c0-0.4,0.32-0.72,0.72-0.72c0.4,0,0.72,0.32,0.72,0.72C41.54,39.82,41.21,40.14,40.81,40.14z" />
			<path d="M31.49,43.38l-2.2,1.28V32.75l10.31-5.99v2.21c0,0.67,0.55,1.22,1.22,1.22s1.22-0.55,1.22-1.22v-4.33c0-0.15-0.04-0.28-0.09-0.42c-0.01-0.03-0.03-0.07-0.04-0.1c-0.06-0.13-0.14-0.24-0.23-0.34c-0.08-0.07-0.16-0.14-0.24-0.2l-12.85-7.41c-0.38-0.22-0.85-0.22-1.22,0l-12.76,7.41c-0.09,0.05-0.17,0.12-0.23,0.19c0,0,0,0-0.01,0.01c-0.1,0.1-0.18,0.22-0.23,0.34c-0.02,0.03-0.03,0.07-0.04,0.1C14.04,24.35,14,24.48,14,24.63v14.75c0,0.44,0.23,0.84,0.61,1.06l12.84,7.41c0.06,0.03,0.12,0.05,0.18,0.07c0.04,0.02,0.08,0.04,0.12,0.05c0.1,0.03,0.21,0.04,0.31,0.04c0.11,0,0.22-0.01,0.31-0.04c0.02,0,0.03-0.01,0.05-0.02c0.09-0.03,0.17-0.06,0.25-0.11l4.04-2.35c0.59-0.34,0.78-1.09,0.44-1.67c-0.34-0.59-1.09-0.78-1.67-0.44L31.49,43.38z M27.97,18.63l10.39,6l-10.32,5.99l-10.39-5.99L27.97,18.63z M16.44,26.74l10.39,6v11.93l-10.39-6V26.74z" />
			<path d="M24.29,32.85l-4.06-2.35c-0.59-0.33-1.33-0.14-1.67,0.45c-0.34,0.59-0.14,1.33,0.45,1.67l4.06,2.35c0.2,0.11,0.4,0.17,0.61,0.17c0.42,0,0.83-0.22,1.06-0.61C25.07,33.95,24.86,33.21,24.29,32.85L24.29,32.85z" />
		</g>
	</svg>
);

// 内联 SVG 图标组件 - 包装与全球物流
const PackagingIcon = ({ className, isActive }: { className?: string; isActive?: boolean }) => (
	<svg viewBox="0 0 64 64" className={className}>
		<circle className={isActive ? "fill-white" : "fill-[#F3F3F3]"} cx="32" cy="32" r="32" />
		<g className={isActive ? "fill-[#65A525]" : "fill-[#8A8A8A]"}>
			<path d="M45.38,23.09l-11.77-6.75c-0.4-0.22-0.85-0.34-1.3-0.34c-0.46,0-0.91,0.12-1.3,0.34l-11.76,6.75c-0.38,0.21-0.69,0.52-0.91,0.89c-0.22,0.37-0.34,0.8-0.34,1.23v13.57c0,0.43,0.12,0.85,0.34,1.23c0.22,0.37,0.53,0.68,0.91,0.89l11.76,6.75c0.4,0.23,0.85,0.34,1.31,0.34s0.91-0.12,1.31-0.34l11.76-6.75c0.37-0.21,0.69-0.52,0.91-0.89c0.22-0.37,0.34-0.8,0.34-1.23V25.21c-0.01-0.43-0.12-0.85-0.34-1.22C46.07,23.61,45.76,23.31,45.38,23.09z M44.16,26.57v12.22c0,0.01,0,0.02-0.01,0.03c-0.01,0.01-0.01,0.02-0.02,0.02l-10.58,6.07V32.46L44.16,26.57z M28.25,20.71l4.01-2.3c0.01-0.01,0.03-0.01,0.05-0.01c0.02,0,0.03,0,0.05,0.01l10.59,6.07l-3.95,2.19L28.25,20.71z M36.5,28.06l-4.27,2.38l-10.63-5.9l4.21-2.41L36.5,28.06z M31.07,32.55V44.9L20.5,38.84c-0.01-0.01-0.02-0.01-0.02-0.02c-0.01-0.01-0.01-0.02-0.01-0.03V26.66L31.07,32.55z" />
			<path d="M36.11,37.35c0.11,0.18,0.27,0.33,0.46,0.43c0.19,0.1,0.4,0.16,0.61,0.16c0.21,0,0.42-0.05,0.6-0.15l3.14-1.74c0.27-0.15,0.48-0.4,0.57-0.69c0.09-0.3,0.06-0.62-0.08-0.89c-0.01-0.02-0.02-0.05-0.04-0.07c-0.17-0.27-0.44-0.47-0.75-0.55c-0.31-0.08-0.64-0.04-0.92,0.11l-3.14,1.74c-0.27,0.15-0.48,0.4-0.57,0.69c-0.09,0.3-0.06,0.62,0.08,0.89L36.11,37.35z" />
			<path d="M25.92,22.13l2.23-1.51l10.78,6.03l-2.6,1.51L25.92,22.13z" />
		</g>
	</svg>
);

// SVG 图标组件映射
const IconMap = {
	design: DesignIcon,
	prototyping: PrototypingIcon,
	production: ProductionIcon,
	packaging: PackagingIcon,
};

// 阶段数据配置
const stagesData: StageItem[] = [
	{
		id: "design",
		titleKey: "designTitle",
		descriptionKey: "designDesc",
		features: [
			{ titleKey: "designFeature1Title", descKey: "designFeature1Desc" },
			{ titleKey: "designFeature2Title", descKey: "designFeature2Desc" },
			{ titleKey: "designFeature3Title", descKey: "designFeature3Desc" },
			{ titleKey: "designFeature4Title", descKey: "designFeature4Desc" },
		],
		image: "/image/new-version/home/end-to-end-precision-manufacturing-solutions.webp",
	},
	{
		id: "prototyping",
		titleKey: "prototypingTitle",
		descriptionKey: "prototypingDesc",
		features: [
			{ titleKey: "prototypingFeature1Title", descKey: "prototypingFeature1Desc" },
			{ titleKey: "prototypingFeature2Title", descKey: "prototypingFeature2Desc" },
			{ titleKey: "prototypingFeature3Title", descKey: "prototypingFeature3Desc" },
			{ titleKey: "prototypingFeature4Title", descKey: "prototypingFeature4Desc" },
		],
		image: "/image/new-version/home/prototyping-small-batch-production.webp",
	},
	{
		id: "production",
		titleKey: "productionTitle",
		descriptionKey: "productionDesc",
		features: [
			{ titleKey: "productionFeature1Title", descKey: "productionFeature1Desc" },
			{ titleKey: "productionFeature2Title", descKey: "productionFeature2Desc" },
			{ titleKey: "productionFeature3Title", descKey: "productionFeature3Desc" },
			{ titleKey: "productionFeature4Title", descKey: "productionFeature4Desc" },
		],
		image: "/image/new-version/home/large-scale-production.webp",
	},
	{
		id: "packaging",
		titleKey: "packagingTitle",
		descriptionKey: "packagingDesc",
		features: [
			{ titleKey: "packagingFeature1Title", descKey: "packagingFeature1Desc" },
			{ titleKey: "packagingFeature2Title", descKey: "packagingFeature2Desc" },
			{ titleKey: "packagingFeature3Title", descKey: "packagingFeature3Desc" },
			{ titleKey: "packagingFeature4Title", descKey: "packagingFeature4Desc" },
		],
		image: "/image/new-version/home/packaging-global-logistics.webp",
	},
];

export default function ProductDevelopment() {
	const t = useTranslations("productDevelopment");
	const tBase = useTranslations("base");

	// 当前激活的阶段索引
	const [activeIndex, setActiveIndex] = useState(0);

	// 获取当前激活阶段的数据
	const activeData = stagesData[activeIndex];

	return (
		<section className="bg-white py-12 sm:py-16">
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-8 text-center sm:mb-10 md:mb-12 lg:mb-16">
					<h2 className="mb-4 text-2xl font-bold text-black sm:mb-5 sm:text-3xl md:mb-6 md:text-4xl lg:text-[42px] lg:leading-tight">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 sm:text-base md:text-lg">{t("description")}</p>
				</div>

				{/* 步骤导航 */}
				<div className="relative mb-8 sm:mb-10 md:mb-12 lg:mb-16">
					{/* 连接线 - 桌面端 */}
					<div className="absolute left-0 right-0 top-6 hidden h-[2px] bg-gray-200 sm:top-7 md:block" />

					{/* 步骤项 */}
					<div className="grid grid-cols-2 gap-4 sm:flex sm:flex-row sm:justify-center sm:gap-6 md:gap-8 lg:gap-10">
						{stagesData.map((stage, index) => {
							const IconComponent = IconMap[stage.id];
							const isActive = index === activeIndex;

							return (
								<button
									key={stage.id}
									onClick={() => setActiveIndex(index)}
									className="flex flex-col items-center gap-2 transition-all duration-300 hover:opacity-80 sm:gap-2"
								>
									{/* 图标容器 */}
									<div
										className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full transition-shadow duration-300 sm:h-12 sm:w-12 md:h-14 md:w-14 ${
											isActive ? "shadow-[0_3px_12px_rgba(0,0,0,0.12)] shadow-green-200" : ""
										}`}
									>
										<IconComponent className="h-full w-full" isActive={isActive} />
									</div>

									{/* 标题 */}
									<span
										className={`max-w-[120px] text-center text-xs font-medium leading-tight transition-colors duration-300 sm:max-w-[140px] sm:text-sm md:max-w-[160px] md:text-base ${
											isActive ? "text-themePrimary400" : "text-gray-600"
										}`}
									>
										{t(stage.titleKey)}
									</span>
								</button>
							);
						})}
					</div>
				</div>

				{/* 内容展示区 */}
				<div className="">
					{/* 阶段标题和描述 */}
					<div className="mb-6 sm:mb-8">
						<h3 className="mb-3 text-xl font-bold text-themePrimary400 sm:mb-4 sm:text-2xl">
							{t(activeData.titleKey)}
						</h3>
						<p className="text-sm leading-relaxed text-gray-900 sm:text-base md:text-lg">
							{t(activeData.descriptionKey)}
						</p>
					</div>

					{/* 图片和特性列表 */}
					<div className="flex flex-col gap-6 lg:flex-row lg:gap-10 xl:gap-12">
						{/* 左侧图片 */}
						<div className="relative w-[300px] overflow-hidden sm:w-[400px] md:w-[500px] lg:w-[480px]">
							<Image
								src={activeData.image}
								alt={t(activeData.titleKey)}
								width={1000}
								height={1000}
								className="w-full object-cover transition-transform duration-500 hover:scale-105"
							/>
						</div>

						{/* 右侧特性列表 */}
						<div className="flex flex-1 flex-col justify-center">
							<ul className="space-y-4 sm:space-y-5 md:space-y-8">
								{activeData.features.map((feature, index) => (
									<li key={index}>
										<div className="mb-8 flex items-center gap-2">
											<CheckCircle
												size={24}
												weight="fill"
												className="flex-shrink-0 text-themePrimary400 sm:h-7 sm:w-7"
											/>
											<h4 className="text-base font-semibold text-gray-900 sm:text-lg">
												{t(feature.titleKey)}
											</h4>
										</div>
										<p className="text-sm leading-relaxed text-gray-900 sm:text-base">{t(feature.descKey)}</p>
									</li>
								))}
							</ul>
						</div>
					</div>
				</div>

				{/* 底部按钮 */}
				<div className="mt-8 flex flex-col items-center justify-center gap-4 sm:mt-10 sm:flex-row sm:gap-6 md:mt-12">
					<Link
						href="/upload"
						className="inline-flex w-full items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-opacity-80 hover:text-white sm:w-auto sm:px-8 sm:py-4"
					>
						{t("startProject")}
					</Link>
				</div>
			</div>
		</section>
	);
}
